import { useCallback, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep'; // <-- Add this import

import { SearchEvent } from 'carbon-react/lib/components/search';
import { flattenTree } from '@futrli/shared-services';

import { COA_TYPE, COADisplayAccount } from '../types';
import { getFlattenTreeData, getFilteredAccounts, getAllParentsMap } from '../utils';

interface UseCOAGridParams {
  treeData: COADisplayAccount | null;
  displayAccountCodes?: boolean;
  onSaveReorder: (draftTree: COADisplayAccount, coaType: COA_TYPE, onSuccess: () => void) => void;
  isBalanceSheet?: boolean;
}

/**
 * Manages the UI state for the Chart of Accounts Data Grid.
 * Handles search filtering, expand/collapse state, and the drag-and-drop reordering draft session.
 */
export const useCOAGrid = ({ treeData, displayAccountCodes, onSaveReorder, isBalanceSheet }: UseCOAGridParams) => {
  // Only holds data when the user is actively reordering. Otherwise null.
  const [draftTree, setDraftTree] = useState<COADisplayAccount | null>(null);

  const [searchText, setSearchText] = useState('');

  const currentTreeToDisplay = draftTree || treeData;
  const isReordering = draftTree !== null;

  const accountsList = useMemo(() => {
    if (!currentTreeToDisplay) {
      return [];
    }
    if (searchText) {
      return getFilteredAccounts(searchText, currentTreeToDisplay);
    }
    return getFlattenTreeData(currentTreeToDisplay.children);
  }, [currentTreeToDisplay, searchText]);

  const nodeIdsWithParentIds = useMemo(() => {
    if (!currentTreeToDisplay) {
      return new Map();
    }
    return getAllParentsMap(currentTreeToDisplay);
  }, [currentTreeToDisplay]);

  const accountNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (currentTreeToDisplay) {
      flattenTree([currentTreeToDisplay]).forEach(child => {
        map[child.id] = displayAccountCodes ? `${child.code} ${child.name}` : child.name;
      });
    }
    return map;
  }, [currentTreeToDisplay, displayAccountCodes]);

  const handleSearchChange = useCallback((e: SearchEvent) => {
    setSearchText(e.target.value);
  }, []);

  const handleStartReordering = useCallback(() => {
    if (treeData) {
      setDraftTree(cloneDeep(treeData));
    }
  }, [treeData]);

  const handleDragAndDropUpdate = useCallback((newTree: COADisplayAccount) => {
    setDraftTree(newTree);
  }, []);

  const handleCancelReordering = useCallback(() => {
    setDraftTree(null);
  }, []);

  const handleSaveReordering = useCallback(() => {
    if (draftTree) {
      onSaveReorder(draftTree, isBalanceSheet ? COA_TYPE.BALANCE_SHEET : COA_TYPE.PROFIT_AND_LOSS, () => {
        setDraftTree(null);
      });
    }
  }, [draftTree, isBalanceSheet, onSaveReorder]);

  return {
    currentTree: currentTreeToDisplay,
    isReordering,
    searchText,
    accountsList,
    accountNameMap,
    nodeIdsWithParentIds,
    handleSearchChange,
    handleStartReordering,
    handleDragAndDropUpdate,
    handleCancelReordering,
    handleSaveReordering
  };
};
